import {HEX_DIRECTIONS} from "../../../constants/geometry";
import {coordsToKey} from "../../coords/coordsToKey";
import {keyToCoords} from "../../coords/keyToCoords";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {FARM_SUPPLY_STANDARD_COUNT, FARM_SUPPLY_WITH_WAREHOUSE_COUNT} from "../../../constants/supply";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {checkIfHexBlockedByEnemyUnit} from "../../gameState/checkIfHexBlockedByEnemyUnit";
import {checkIfCityHasWorkingArsenal} from "../../gameState/recruitment/checkIfCityHasWorkingArsenal";
import {checkIfAdjacentToWorkingMilitaryCamp} from "../../gameState/specialBuildings/checkIfAdjacentToWorkingMilitaryCamp";

// Волна 33 («сильные города», см. configs/botBonusBehavior): чистые правила
// найма бонусного бота с пакетом strongCities. Раньше единственный бонусный
// хук найма (bonusProfileForHire в decideRecruitmentStep) РАССЕИВАЛ армию:
// подкрепление уходило в очередной город без гарнизона у чужих границ, по
// одному отряду на город при потолке 4. Здесь — обратный порядок: казармы
// кузницы (город-плацдарм оси удара) первыми, фронтир — только когда он и
// пуст, и под угрозой, остальное — по близости к кварталам ближайшего
// живого противника, а не к любому чужому городу (включая нейтральные).
//
// Модуль без Phaser/стора, чтобы правила покрывались юнит-тестами напрямую.

// Радиус «города с гарнизоном» — тот же, что у прежнего бонусного хука:
// свой отряд в 1 ячейке от любого квартала (казарма рядом с кварталом)
const GARRISON_RADIUS = 1;

// Ранги казарм при найме нового отряда (меньше — раньше)
export const STRONG_CITIES_BARRACKS_RANKS = {
  FORGE_NEAR_CAMP: 0, // казарма кузницы, смежная рабочему военному лагерю
  FORGE: 1, // остальные казармы кузницы
  THREATENED_FRONTIER: 2, // фронтирный город без гарнизона и под угрозой
  OTHER: 3,
};

// Гексагональная дистанция по осевым координатам
const calcHexDistance = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

// Ключи шести соседних ячеек (существование на поле не проверяется: соседа
// вне поля просто нет ни в specialBuildings, ни среди казарм)
const getAdjacentHexKeys = (hexKey) => {
  const [q, r] = keyToCoords(hexKey);
  return HEX_DIRECTIONS.map(([dq, dr]) => coordsToKey(q + dq, r + dr));
};

/**
 * Суммарная емкость снабжения своих ферм: обычная ферма кормит один отряд,
 * ферма, смежная складу, — два (FARM_SUPPLY_WITH_WAREHOUSE_COUNT, правило
 * calcFarmRemainingSupply). Склад берется из specialBuildings как есть, без
 * проверки блокировки: потолок найма — это план на раунд, а не расчет фазы
 * снабжения. Это H5: фермерский потолок бонусного бота считается по емкости,
 * а не по числу ферм, чтобы тыловой кластер «склад + фермы» реально кормил
 * больше отрядов, чем россыпь ферм по фронтирным городам.
 */
export const calcFarmsSupplyCapacity = (playerCities, specialBuildings) => {
  let capacity = 0;
  (playerCities || []).forEach((city) => {
    (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach((farmHexKey) => {
      const isNearWarehouse = getAdjacentHexKeys(farmHexKey).some(
        (adjacentKey) => specialBuildings?.[adjacentKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
      );
      capacity += isNearWarehouse ? FARM_SUPPLY_WITH_WAREHOUSE_COUNT : FARM_SUPPLY_STANDARD_COUNT;
    });
  });
  return capacity;
};

/** Есть ли свой отряд в GARRISON_RADIUS от любого квартала города */
const checkIfCityHasGarrison = (city, units, currentPlayerId) => {
  const cityQuarters = getAllQuartersOfCity(city);
  return units.some((unit) =>
    unit.ownerId === currentPlayerId
    && cityQuarters.some((quarterHexKey) => calcHexDistance(quarterHexKey, unit.hexKey) <= GARRISON_RADIUS)
  );
};

/** Есть ли отряд противника (не нейтрала) в threatRadius от любого квартала города */
const checkIfCityUnderThreat = (city, units, currentPlayerId, threatRadius) => {
  const cityQuarters = getAllQuartersOfCity(city);
  return units.some((unit) =>
    unit.ownerId !== currentPlayerId
    && unit.ownerId !== NEUTRAL_PLAYER_ID
    && cityQuarters.some((quarterHexKey) => calcHexDistance(quarterHexKey, unit.hexKey) <= threatRadius)
  );
};

/**
 * Казармы кузницы: все — в forgeBarracks, смежные рабочему военному лагерю
 * (лагерь не заблокирован отрядом противника и не поврежден) — еще и в
 * campAdjacentBarracks. Кузница — свой город с centralQuarter ===
 * stagingCityKey; если ее нет среди своих городов (ось устарела), оба
 * множества пусты.
 */
export const collectForgeBarracks = ({
  playerCities, stagingCityKey, currentPlayerId, units, hexes, specialBuildings, buildingsDamage,
}) => {
  const forgeBarracks = new Set();
  const campAdjacentBarracks = new Set();
  const forgeCity = (playerCities || []).find((city) => city.centralQuarter === stagingCityKey);
  if (!forgeCity) {
    return {forgeBarracks, campAdjacentBarracks};
  }
  (forgeCity[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => forgeBarracks.add(barracksHexKey));
  (forgeCity[SPECIAL_BUILDING_TOKENS.MILITARY_CAMP] || []).forEach((campHexKey) => {
    const isWorking = !checkIfHexBlockedByEnemyUnit(
      campHexKey, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP, currentPlayerId, units, hexes, specialBuildings, buildingsDamage
    );
    if (!isWorking) {
      return;
    }
    getAdjacentHexKeys(campHexKey).forEach((adjacentKey) => {
      if (forgeBarracks.has(adjacentKey)) {
        campAdjacentBarracks.add(adjacentKey);
      }
    });
  });
  return {forgeBarracks, campAdjacentBarracks};
};

/**
 * Казармы фронтирных городов, которые одновременно без гарнизона (нет своего
 * отряда в 1 ячейке от квартала) и под угрозой (отряд противника в
 * threatRadius от квартала). Только такой город получает подкрепление раньше
 * прочих: пустой, но никем не тронутый фронтирный город больше не оттягивает
 * найм от кузницы. classifyCity — classifyOwnCity с осью текущего раунда.
 */
export const collectThreatenedUndefendedFrontierBarracks = ({
  playerCities, classifyCity, units, currentPlayerId, threatRadius,
}) => {
  const barracks = new Set();
  (playerCities || []).forEach((city) => {
    if (classifyCity(city) !== 'frontier') {
      return;
    }
    if (checkIfCityHasGarrison(city, units, currentPlayerId)) {
      return;
    }
    if (!checkIfCityUnderThreat(city, units, currentPlayerId, threatRadius)) {
      return;
    }
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => barracks.add(barracksHexKey));
  });
  return barracks;
};

/** Ранг казармы для порядка найма нового отряда (см. STRONG_CITIES_BARRACKS_RANKS) */
export const getStrongCitiesBarracksRank = (cellKey, {forgeBarracks, campAdjacentBarracks, threatenedFrontierBarracks}) => {
  if (forgeBarracks.has(cellKey)) {
    return campAdjacentBarracks.has(cellKey)
      ? STRONG_CITIES_BARRACKS_RANKS.FORGE_NEAR_CAMP
      : STRONG_CITIES_BARRACKS_RANKS.FORGE;
  }
  if (threatenedFrontierBarracks.has(cellKey)) {
    return STRONG_CITIES_BARRACKS_RANKS.THREATENED_FRONTIER;
  }
  return STRONG_CITIES_BARRACKS_RANKS.OTHER;
};

/**
 * H1: порядок казарм для найма нового отряда у бота с пакетом strongCities.
 * Сначала ранг (кузница у лагеря → кузница → пустой фронтир под угрозой →
 * остальные), внутри ранга — ближе к кварталам enemyColor (ось удара), при
 * равенстве — исходный порядок (сортировка стабильна). Возвращает новый массив.
 * Заслон волны 32 (казармы городов под риском захвата) накладывается поверх
 * этого порядка уже в decideRecruitmentStep.
 */
export const sortBarracksForStrongCities = (cells, {
  forgeBarracks, campAdjacentBarracks, threatenedFrontierBarracks, enemyColorDistances,
}) => {
  const groups = {forgeBarracks, campAdjacentBarracks, threatenedFrontierBarracks};
  const distanceOf = (cellKey) => enemyColorDistances?.get(cellKey) ?? Infinity;
  return cells
    .map((cellKey, index) => ({cellKey, index, rank: getStrongCitiesBarracksRank(cellKey, groups), distance: distanceOf(cellKey)}))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.index - b.index;
    })
    .map((entry) => entry.cellKey);
};

/**
 * Волна 37 (A, боты «золото+»): казармы своих городов, смежные рабочему
 * военному лагерю, — по правилу роста в снабжении
 * (checkIfAdjacentToWorkingMilitaryCamp: на казарме у такого лагеря отряд растет
 * на 4 вместо 2; владелец лагеря правилом не проверяется). В отличие от
 * collectForgeBarracks — у всех своих городов, а не только у кузницы, и без оси
 * удара (она есть только у LARGE+). Возвращает Set ячеек казарм.
 */
export const collectCampAdjacentBarracks = ({
  playerCities, currentPlayer, units, hexes, specialBuildings, citiesCache, buildingsDamage,
}) => {
  const barracks = new Set();
  (playerCities || []).forEach((city) => {
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => {
      if (!barracks.has(barracksHexKey) && checkIfAdjacentToWorkingMilitaryCamp(
        barracksHexKey, currentPlayer, hexes, specialBuildings, citiesCache, units, buildingsDamage
      )) {
        barracks.add(barracksHexKey);
      }
    });
  });
  return barracks;
};

/**
 * H4: казармы своих городов с РАБОЧИМ арсеналом — там докупка размера дает
 * 2 единицы за квартал вместо 1 (calcCoinsRecruitmentSize), поэтому бонусные
 * монеты конвертируются в силу вдвое быстрее. Возвращает Set ячеек казарм.
 */
export const collectArsenalCityBarracks = ({playerCities, currentPlayerId, units, hexes, specialBuildings, buildingsDamage}) => {
  const barracks = new Set();
  (playerCities || []).forEach((city) => {
    if (!checkIfCityHasWorkingArsenal(city, currentPlayerId, units, hexes, specialBuildings, buildingsDamage)) {
      return;
    }
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((barracksHexKey) => barracks.add(barracksHexKey));
  });
  return barracks;
};
